import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Optional,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {NavigationStart, Router} from '@angular/router';
import Quagga, {QuaggaJSCodeReader, QuaggaJSConfigObject, QuaggaJSReaderConfig, QuaggaJSResultObject} from '@ericblade/quagga2';
import {Subject} from 'rxjs';
import {distinctUntilChanged, filter, takeUntil} from 'rxjs/operators';
import {Detachable} from '../../routing/AppRouterOutletDirective';
import {DEFAULT_CONFIG} from './scanner.config';

@Component({
  selector: 'app-scanner',
  templateUrl: './scanner.component.html',
  styleUrls: ['./scanner.component.scss']
})
export class ScannerComponent implements OnChanges, OnDestroy, Detachable {
  private readonly destroy$ = new Subject<void>();

  @Input() type: string | string[];
  @Input() deviceId: string;

  @Output() scanSuccess = new EventEmitter<QuaggaJSResultObject>();
  @Output() started = new EventEmitter<boolean>();

  @ViewChild('scanner', { static: true }) scanner: ElementRef<HTMLDivElement>;

  private _started = false;
  private _isInitializing = false;
  private readonly CERTAINTY = 0.9;
  private scannedSubject: Subject<QuaggaJSResultObject> = new Subject<QuaggaJSResultObject>();

  private readonly onProcessedHandler = (result: QuaggaJSResultObject) => this.onProcessed(result);
  private readonly onDetectedHandler = (result: QuaggaJSResultObject) => this.onDetected(result);

  constructor(@Optional() private router?: Router) {
    this.scannedSubject
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged((prev, curr) => prev?.codeResult?.code === curr?.codeResult?.code)
      )
      .subscribe(result => this.scanSuccess.emit(result));

    if (this.router) {
      this.router.events
        .pipe(
          filter(event => event instanceof NavigationStart),
          takeUntil(this.destroy$)
        )
        .subscribe(() => {
          this.stop();
        });
    }
  }

  get isStarted(): boolean {
    return this._started;
  }

  ngOnDestroy(): void {
    this.stop();
    this.destroy$.next();
    this.destroy$.complete();
  }

  onDetach(): void {
    this.stop();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this._started && (changes.type || changes.deviceId)) {
      this.restart();
    }
  }

  private buildConfig(): QuaggaJSConfigObject {
    const config: QuaggaJSConfigObject = JSON.parse(JSON.stringify(DEFAULT_CONFIG));

    if (this.scanner?.nativeElement) {
      config.inputStream.target = this.scanner.nativeElement;
    }

    if (this.deviceId) {
      config.inputStream.constraints = {
        ...config.inputStream.constraints,
        deviceId: this.deviceId
      };
    }

    if (this.type) {
      const types = Array.isArray(this.type) ? this.type : [this.type];
      const readers: (QuaggaJSReaderConfig | QuaggaJSCodeReader)[] = types.map(t => {
        const formatted = t.endsWith('_reader') ? t : `${t}_reader`;
        return formatted as QuaggaJSCodeReader;
      });
      if (readers.length > 0) {
        config.decoder = {
          ...config.decoder,
          readers
        };
      }
    }

    return config;
  }

  private _init(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.detachQuaggaListeners();

      Quagga.onProcessed(this.onProcessedHandler);
      Quagga.onDetected(this.onDetectedHandler);

      const config = this.buildConfig();

      Quagga.init(config, (err: any) => {
        if (err) {
          console.error('Quagga initialization failed:', err);
          this.detachQuaggaListeners();
          return reject(err);
        }
        resolve();
      });
    });
  }

  async start(): Promise<void> {
    if (this._started || this._isInitializing) {
      return;
    }

    this._isInitializing = true;
    try {
      await this._init();
      if (this.destroy$.isStopped || !this._isInitializing) {
        this.stop();
        return;
      }
      Quagga.start();
      this._started = true;
      this.started.emit(true);
    } catch (error) {
      this._started = false;
      this.started.emit(false);
      this.stop();
      throw error;
    } finally {
      this._isInitializing = false;
    }
  }

  pause(): void {
    if (this._started) {
      this._started = false;
      this.started.emit(false);
      try {
        Quagga.pause();
      } catch (e) {
        console.warn('Error pausing Quagga:', e);
      }
      this.detachQuaggaListeners();
    }
  }

  stop(): void {
    this._isInitializing = false;
    this.detachQuaggaListeners();

    try {
      Quagga.stop();
    } catch (e) {
      // Ignored if Quagga is not currently running
    }

    this.releaseMediaTracks();

    if (this._started) {
      this._started = false;
      this.started.emit(false);
    }
  }

  async restart(): Promise<void> {
    this.stop();
    await this.start();
  }

  private detachQuaggaListeners(): void {
    try {
      Quagga.offProcessed(this.onProcessedHandler);
      Quagga.offDetected(this.onDetectedHandler);
    } catch (e) {
      // Ignored if handlers were not attached
    }
  }

  private releaseMediaTracks(): void {
    try {
      if (this.scanner?.nativeElement) {
        const videoElement = this.scanner.nativeElement.querySelector('video') as HTMLVideoElement;
        if (videoElement && videoElement.srcObject) {
          const stream = videoElement.srcObject as MediaStream;
          stream.getTracks().forEach(track => track.stop());
          videoElement.srcObject = null;
        }
      }
    } catch (e) {
      console.warn('Error releasing media tracks:', e);
    }
  }

  onProcessed(result: QuaggaJSResultObject): void {
    const drawingCtx = Quagga.canvas?.ctx?.overlay;
    const drawingCanvas = Quagga.canvas?.dom?.overlay;

    if (drawingCtx && drawingCanvas && result) {
      const width = parseInt(drawingCanvas.getAttribute('width') || '0', 10);
      const height = parseInt(drawingCanvas.getAttribute('height') || '0', 10);

      if (width > 0 && height > 0) {
        drawingCtx.clearRect(0, 0, width, height);
      }

      if (result.boxes) {
        result.boxes
          .filter(box => box !== result.box)
          .forEach(box => Quagga.ImageDebug.drawPath(box, { x: 0, y: 1 }, drawingCtx, { color: 'green', lineWidth: 2 }));
      }

      if (result.box) {
        Quagga.ImageDebug.drawPath(result.box, { x: 0, y: 1 }, drawingCtx, { color: '#00F', lineWidth: 2 });
      }

      if (result.codeResult && result.codeResult.code && result.line) {
        Quagga.ImageDebug.drawPath(result.line, {
          x: 'x',
          y: 'y',
        }, drawingCtx, {
          color: 'red',
          lineWidth: 3,
        });
      }
    }
  }

  onDetected(result: QuaggaJSResultObject): void {
    if (result?.codeResult?.code && this.getMedianOfCodeErrors(result) < (1 - this.CERTAINTY)) {
      this.scannedSubject.next(result);
    }
  }

  private getMedianOfCodeErrors(result: QuaggaJSResultObject): number {
    if (!result?.codeResult?.decodedCodes) {
      return 0;
    }
    const errors = result.codeResult.decodedCodes
      .filter(x => x && x.error !== undefined)
      .map(x => x.error as number);
    return this.getMedian(errors);
  }

  private getMedian(arr: number[]): number {
    if (!arr || arr.length === 0) {
      return 0;
    }
    const sorted = [...arr].sort((a, b) => a - b);
    const half = Math.floor(sorted.length / 2);
    if (sorted.length % 2 === 1) {
      return sorted[half];
    }
    return (sorted[half - 1] + sorted[half]) / 2;
  }
}
